import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { GlobalNav } from "../shared/GlobalNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeaturesPage = () => {
  const { user, logout } = useAuth();
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const location = useLocation();

  useEffect(() => {
    fetchSubscriptionFeatures();
  }, [location]);

  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await axios.get(`${API}/subscription/features`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setSubscriptionFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription features:', error);
    }
  };

  const currentPlanName = subscriptionFeatures?.plan_info?.name || 'Standard';
  const currentPlan = subscriptionFeatures?.subscription_plan || 'standard';

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="features" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 font-heading">Key Features</h1>
          <div className="max-w-3xl mx-auto">
            <p className="text-xl text-gray-700 mb-4 font-body">
              Paify is the <em>one-stop solution</em> for service businesses to get paid on time, every time.
            </p>
            <p className="text-gray-600 font-body leading-relaxed">
              Our structured checkout brings quotes, contract-backed invoicing, the VAT Vault, and our Paify System together in one place—so you run every project with clarity, speed, and control. Automations replace chasing, fixed payment options kill delays, and airtight records keep you organised. Maximise efficiency, accelerate cash flow, and make late payments history.
            </p>
          </div>
        </div>

        <div className="mb-12">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">
              {currentPlan === 'standard' ? 'Included in your Standard Plan' : 'Standard Plan Features'}
            </h2>
            <p className="text-gray-600 font-body">Your complete Paify system.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: "🛒", title: "Structured checkout (5 options)", desc: "Upfront, 50/50, Monthly Instalments, Credit Card Payment, Buy-Now-Pay-Later (Coming Soon).", tag: "Standard" },
              { icon: "📄", title: "Smart contracts", desc: "Pre-set terms, non-negotiable structure; PDF + audit trail.", tag: "Standard" },
              { icon: "📧", title: "All-in-one emailing", desc: "Payment link → contract → checkout; confirmations, reminders, completion + Google review.", tag: "Standard" },
              { icon: "🌐", title: "Hosted link", desc: "Share a secure checkout in seconds.", tag: "Standard" },
              { icon: "📊", title: "Provider dashboard", desc: "Status, schedules, receipts, contracts, exports.", tag: "Standard" },
              { icon: "📱", title: "Mobile-friendly", desc: "Works great on phones; App coming soon.", tag: "Standard" },
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-lg p-6 shadow-sm border">
                <div className="flex items-start space-x-4">
                  <div className="text-2xl">{feature.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">{feature.tag}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 font-heading">{feature.title}</h3>
                    <p className="text-gray-600 font-body text-sm">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">
              {currentPlan === 'paidd_plus' ? 'Included in your Paify Plus Plan' : 'Paify Plus Features'}
            </h2>
            <p className="text-gray-600 font-body">
              {currentPlan === 'paidd_plus' || currentPlan === 'embedded' 
                ? 'Everything in the Standard plan, plus:' 
                : 'Upgrade to unlock these features:'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: "🖼️", title: "Brand & logo uploads", desc: "Your logo + colours on invoices, contracts, receipts, emails." },
              { icon: "🧾", title: "VAT receipts", desc: "Instant VAT-ready receipts for both sides." },
              { icon: "🔔", title: "Reminders & dunning", desc: "Deposit/balance nudges; card update & 'pay now' links." },
              { icon: "💰", title: "Invoices", desc: "Professional invoice generation, tracking, and management." },
              { icon: "📝", title: "Quotes", desc: "Create, send, and manage professional quotes with acceptance tracking." },
              { icon: "🔄", title: "Recurring Pay", desc: "Built for subscriptions, retainers and ongoing service agreements with automatic invoices.", isNew: true },
              { icon: "⏱️", title: "Late-payment interest", desc: "Statutory interest + compensation calculator; one-click notices." },
              { icon: "✅", title: "Certified Paify badge", desc: "Display the Paify Certified badge on your website to signal structure and fair terms to clients." },
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-lg p-6 shadow-sm border">
                <div className="flex items-start space-x-4">
                  <div className="text-2xl">{feature.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                      {feature.isNew && <span className="inline-block px-2 py-1 text-xs font-medium bg-pink-100 text-pink-800 rounded">New</span>}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 font-heading">{feature.title}</h3>
                    <p className="text-gray-600 font-body text-sm">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">
              {currentPlan === 'embedded' ? 'Included in your Embedded Plan' : 'Embedded Plan Features'}
            </h2>
            <p className="text-gray-600 font-body">Everything in Paify Plus, plus website integration.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <div className="flex items-start space-x-4">
                <div className="text-2xl">🧩</div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Embedded</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 font-heading">Embedded checkout</h3>
                  <p className="text-gray-600 font-body text-sm">Fully branded checkout on your website.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Paify Add Ons</h2>
            <p className="text-gray-600 font-body">Add Ons are available with Paify Plus and Embedded Plans only.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <div className="flex items-start space-x-4">
                <div className="text-2xl">🏦</div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">Add on</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 font-heading">VAT Vault</h3>
                  <p className="text-gray-600 font-body text-sm">Auto-allocate % to a VAT pot; monthly summary & export. £9.99/month.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <div className="flex items-start space-x-4">
                <div className="text-2xl">🛡️</div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">Add on</span>
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">Coming Soon</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 font-heading">Invoice Insurance</h3>
                  <p className="text-gray-600 font-body text-sm">Partner-backed cover for larger projects. £9.99/month.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FeaturesPage;
